import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { SiteNumberFunc } from '@ecp/common/src/const/site/siteNumberConst';
import { setCookie } from '@ecp/common/src/utils/cookieUtils';
import { FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { PageButton } from '@ecp/common/src/components/button/Button';
import Input from '@ecp/common/src/components/input/Input';

import useSiteInfo from '@fo-hooks/common/useSiteInfo';
import { useAlertDialog } from '@fo-hooks/common/useModal';
import gatePageMopImage from '@ecp/common/src/assets/image/mop_pc_gate_page.jpg';
import authApi from '@fo-apis/common/authApi';

/**
 * MOP 우대코드 확인 페이지
 */
const MopGateCodePage = ({ onSuccess }) => {
  const { showAlertDialog } = useAlertDialog();
  const { siteNumber } = useSiteInfo();

  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const cookieGateName = SiteNumberFunc.cookieGateName(siteNumber);

  const handleChangeCode = useCallback((event) => {
    event.target.value = event.target.value.replace(/\s/g, '');
    setCode(event.target.value);
  }, []);

  const handleSubmit = async () => {
    const trimmedCode = code.trim();

    if (!trimmedCode) {
      showAlertDialog('우대코드를 입력해 주세요.');
      return;
    }

    setIsSubmitting(true);

    try {
      const { result } = await authApi.getEventMallSpeByCode(trimmedCode);

      if (result) {
        const cookieValue = result + '|' + trimmedCode;

        setCookie(cookieGateName, cookieValue, {
          path: '/',
        });

        onSuccess?.();
      } else {
        showAlertDialog('유효한 우대코드가 아닙니다.');
      }
    } catch (error) {
      showAlertDialog(error?.response?.data?.message || '유효한 우대코드가 아닙니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageContainer width={'100%'} align-items={'center'}>
      <MopImageWrapper>
        <MopImage src={gatePageMopImage} alt={'웰스토리 고객사 복지몰 안내 이미지'} />
        <CodeFormArea>
          <CodeInput
            value={code}
            dataKey="code"
            styleProps={{ width: '360px', height: '50px' }}
            onChange={handleChangeCode}
            placeholder={'우대코드를 입력해 주세요'}
            title={'우대코드를 입력해 주세요'}
            id={'mop_gate_code'}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !isSubmitting) {
                handleSubmit();
              }
            }}
          />
          <Spacing top={20} />
          <PageButton width={'360px'} height={'60px'} onClick={handleSubmit} disabled={isSubmitting}>
            입장하기
          </PageButton>
        </CodeFormArea>
      </MopImageWrapper>
      <Spacing top={20} />
    </PageContainer>
  );
};

MopGateCodePage.propTypes = {
  onSuccess: PropTypes.func,
};

const PageContainer = styled(FlexBoxColumn)`
  width: 100%;
  align-items: center;
  padding-top: 30px;
`;

const MopImageWrapper = styled.div`
  position: relative;
  width: 1400px;
  max-width: calc(100% - 80px);
`;

const MopImage = styled.img`
  display: block;
  width: 100%;
`;

const CodeFormArea = styled(FlexBoxColumn)`
  position: absolute;
  left: 50%;
  bottom: 20%;
  align-items: center;
  transform: translateX(-50%);
`;

const CodeInput = ({ value, dataKey = 'code', styleProps, placeholder, onChange, title, id, onKeyDown }) => {
  return (
    <Input
      clearable
      height={styleProps?.height}
      width={styleProps?.width || '100%'}
      minWidth={styleProps?.minWidth}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      data-key={dataKey}
      outlineAble={true}
      title={title}
      id={id}
      onKeyDown={onKeyDown}
    />
  );
};

CodeInput.propTypes = {
  value: PropTypes.string,
  dataKey: PropTypes.string,
  styleProps: PropTypes.object,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  title: PropTypes.string,
  id: PropTypes.string,
  onKeyDown: PropTypes.func,
};

export default MopGateCodePage;
